/**
 * PlatformProfileLayout — hero banner + sticky bar + tab shell for the
 * platform profile (§3 IA, §4 Hero of the spec).
 *
 * Tabs: Summary | Revenue | Subscribers | Catalog Insights
 * URL routing: /platforms/:id/<tab>?region=UCAN
 * Print mode (/platforms/:id/pdf): the same hero without the sticky bar, tabs
 * or controls; the outlet renders every tab stacked (PlatformProfilePrint).
 *
 * Data is loaded here via usePlatformProfile and flows to tabs through
 * React context (no prop drilling, no duplicate fetches thanks to RQ dedup).
 */
import { useRef, useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Outlet } from 'react-router-dom';
import { ArrowLeft, Download, Globe } from 'lucide-react';
import { PlatformIcon } from '@/components/primitives/PlatformIcon';
import { Select } from '@/components/primitives/Select';
import { PlatformSwitcher } from '@/components/organisms/PlatformSwitcher';
import { StatWidget } from '@/components/organisms/StatWidget';
import { Delta } from '@/components/primitives';
import { UniverseTabs } from '@/components/templates/UniverseTabs';
import { formatCount, formatUsdCompact, formatArpu, formatPct, prettyQuarter } from '@/lib/format';
import { usePlatformProfile, type PlatformSummary } from './usePlatformProfile';
import { PlatformProfileSkeleton } from './PlatformProfileSkeleton';
import styles from './PlatformProfileLayout.module.css';

// ─── Context — shared data for all tabs ─────────────────────────────────────

interface ProfileContextValue {
  id: string;
  region: string | undefined;
  /** Sets ?region= ('' clears it). */
  setRegion: (region: string) => void;
  summary: PlatformSummary | undefined;
  profile: ReturnType<typeof usePlatformProfile>;
}
const ProfileContext = createContext<ProfileContextValue | null>(null);

export function usePlatformProfileContext() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('usePlatformProfileContext must be used inside PlatformProfileLayout');
  return ctx;
}

// ─── CDN helpers ─────────────────────────────────────────────────────────────

const BANNER_CDN = (name: string) =>
  `https://cdn-fastly.parrotanalytics.com/general-assets/platform-banners/${encodeURIComponent(name)}.jpg`;

// ─── Region options ───────────────────────────────────────────────────────────

const REGION_OPTIONS = [
  { value: '', label: 'All regions' },
  { value: 'UCAN', label: 'UCAN' },
  { value: 'EMEA', label: 'EMEA' },
  { value: 'APAC', label: 'APAC' },
  { value: 'LATAM', label: 'LATAM' },
];

// ─── Tab definitions ─────────────────────────────────────────────────────────

const PROFILE_TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'subscribers', label: 'Subscribers' },
  { id: 'catalog-insights', label: 'Catalog Insights' },
];

// ─── Layout component ─────────────────────────────────────────────────────────

export interface PlatformProfileLayoutProps {
  /** Print route: static hero, no sticky bar, tabs or controls. */
  print?: boolean;
}

export function PlatformProfileLayout({ print = false }: PlatformProfileLayoutProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const platformName = id ? decodeURIComponent(id) : '';
  const region = searchParams.get('region') || undefined;

  const profile = usePlatformProfile(platformName, region);
  const {
    summaryData: summary,
    ranks,
    rankMovement,
    current,
    changes,
    history,
    dataSpan,
    latestQuarter,
    titleCount,
    isLoading,
    notFound,
  } = profile;

  // ── Sticky bar intersection observer ──────────────────────────────────────
  const heroEndRef = useRef<HTMLDivElement>(null);
  const [heroGone, setHeroGone] = useState(false);
  useEffect(() => {
    const sentinel = heroEndRef.current;
    if (!sentinel) return;
    const root = sentinel.closest('main');
    const io = new IntersectionObserver(
      ([entry]) => setHeroGone(!entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0)),
      { root, threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [isLoading, !!summary]);

  // ── Banner image: hidden on 404 (Crunchyroll has none), gradient remains ──
  const [bannerError, setBannerError] = useState(false);
  useEffect(() => setBannerError(false), [platformName]);

  const handleRegion = (val: string) => {
    setSearchParams(val ? { region: val } : {}, { replace: true });
  };

  if (isLoading) return <PlatformProfileSkeleton />;

  if (notFound || !summary) {
    return (
      <div className={styles.notFound}>
        <div className={styles.notFoundInner}>
          <PlatformIcon name={platformName} size={64} />
          <h2 className={styles.notFoundTitle}>{platformName}</h2>
          <p className={styles.notFoundMsg}>No streaming metrics for {platformName} yet.</p>
          <button onClick={() => navigate('/platforms/leaderboard')} className={styles.backBtn}>
            ← Back to Platforms
          </button>
        </div>
      </div>
    );
  }

  const latestFormatted = latestQuarter ? prettyQuarter(latestQuarter) : '';
  const prevFormatted = history.length > 1 ? prettyQuarter(history[history.length - 2].quarter) : '';
  const regionLabel = region ?? 'All regions';
  const scopeLine = [latestFormatted, regionLabel].filter(Boolean).join(', ');
  const rankScope = `among ${ranks.total} platforms, ${scopeLine}`;

  /* The widget draws its own "$", so money values go in without it. */
  const noSign = (v: string) => v.replace(/^\$/, '');
  const subValue = current ? formatCount(current.subscribers) : '—';
  const revValue = current ? noSign(formatUsdCompact(current.revenue)) : '—';
  const arpuValue = current ? noSign(formatArpu(current.arpu)) : '—';
  const churnValue = current?.churnRate != null ? formatPct(current.churnRate) : '—';

  /** QoQ change, for the ⓘ hint — the widget body stays value · rank · trend. */
  const qoq = (pct: number | undefined, invert = false) =>
    pct === undefined || !prevFormatted ? null : (
      <>
        {' '}
        <Delta value={pct} digits={Math.abs(pct) >= 10 ? 0 : 1} invert={invert} /> on {prevFormatted}.
      </>
    );

  // Trend series (oldest → newest). Churn skips the first quarter, which has
  // no churn figure (LAG artefact, nulled in the hook).
  const trendLabels = history.map((h) => prettyQuarter(h.quarter));
  const churnHistory = history.filter((h) => h.churn_rate != null);

  // Platform switcher — platforms with streaming metrics, ranked by subscribers
  // in the current region scope (the same order as the hero's rank badge).
  const switcherOptions = [...(profile.leaderboard.data ?? [])]
    .sort((a, b) => b.subscribers - a.subscribers)
    .map((r) => ({ name: r.platform, meta: formatCount(r.subscribers) }));
  const currentTab = location.pathname.split('/').pop() || 'summary';

  /* Export: the print route opens in a new tab, waits for every chart's data,
     then opens the browser's print dialog ("Save as PDF"). */
  const openExport = () =>
    window.open(`/platforms/${encodeURIComponent(platformName)}/pdf${location.search}`, '_blank', 'noopener');
  const exportButton = (
    <button type="button" onClick={openExport} className={styles.exportButton} aria-label="Export to PDF">
      <Download size={15} aria-hidden="true" />
      <span className={styles.exportLabel}>Export to PDF</span>
    </button>
  );
  const exportedOn = new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <ProfileContext.Provider value={{ id: platformName, region, setRegion: handleRegion, summary, profile }}>
      <div className={styles.page} data-print={print || undefined}>
        {/* ── Sticky compact header ─────────────────────────────────────── */}
        {!print && (
        <div className={styles.stickyAnchor}>
          <div className={styles.stickyBar} data-visible={heroGone || undefined} aria-hidden={!heroGone}>
            <PlatformIcon name={platformName} size={32} className={styles.stickyIcon} />
            <span className={styles.stickyTitle}>{platformName}</span>
            <span className={styles.stickyMeta}>{scopeLine}</span>
            <div className={styles.stickyControls}>
              <Select
                value={region ?? ''}
                options={REGION_OPTIONS}
                onChange={handleRegion}
                label="Region"
                highlightWhenSet
                autoWidth
                icon={<Globe size={15} aria-hidden="true" />}
                aria-label="Filter by region"
              />
              {exportButton}
            </div>
          </div>
        </div>
        )}

        {/* ── Hero Banner ───────────────────────────────────────────────── */}
        <div className={styles.hero}>
          {!bannerError && (
            <img
              key={platformName}
              src={BANNER_CDN(platformName)}
              alt=""
              aria-hidden="true"
              className={styles.heroBannerImg}
              onError={() => setBannerError(true)}
            />
          )}
          <div className={styles.heroGradient} />

          <div className={styles.heroContent}>
            {print ? (
              <div className={styles.heroTop}>
                <span className={styles.printScope}>Platform profile · {scopeLine}</span>
                <span className={styles.printScope}>Exported {exportedOn} · Parrot Analytics</span>
              </div>
            ) : (
            <div className={styles.heroTop}>
              <button onClick={() => navigate(-1)} className={styles.backButton}>
                <ArrowLeft size={16} /> Back
              </button>
              {/* Page-level scope: every widget, rank and tab follows it. */}
              <div className={styles.heroControls}>
              <Select
                value={region ?? ''}
                options={REGION_OPTIONS}
                onChange={handleRegion}
                label="Region"
                highlightWhenSet
                autoWidth
                icon={<Globe size={15} aria-hidden="true" />}
                aria-label="Filter by region"
              />
              {exportButton}
              </div>
            </div>
            )}

            <div className={styles.heroMain}>
              <div className={styles.heroLogo}>
                <PlatformIcon name={platformName} size={144} />
              </div>

              <div className={styles.heroInfo}>
                <div className={styles.heroTitleRow}>
                  <h1 className={styles.heroName}>
                    {switcherOptions.length > 1 && !print ? (
                      <PlatformSwitcher
                        value={platformName}
                        options={switcherOptions}
                        listLabel={`By subscribers · ${regionLabel}`}
                        onChange={(val) => {
                          if (val === platformName) return;
                          const qs = region ? `?region=${region}` : '';
                          navigate(`/platforms/${encodeURIComponent(val)}/${currentTab}${qs}`);
                        }}
                      />
                    ) : (
                      platformName
                    )}
                  </h1>
                  {ranks.subscribers !== undefined && (
                    <span className={styles.heroRank} title={`Ranked by subscribers ${rankScope}`}>
                      <span className={styles.heroRankPound}>#</span>
                      {ranks.subscribers}
                      <span className={styles.heroRankLabel}>by subscribers</span>
                    </span>
                  )}
                </div>

                <div className={styles.heroFacts}>
                  {summary.region_count > 0 && (
                    <span>
                      {summary.region_count} region{summary.region_count !== 1 ? 's' : ''}
                    </span>
                  )}
                  {dataSpan && <span>{dataSpan}</span>}
                  {titleCount != null && <span>{titleCount.toLocaleString()} titles</span>}
                </div>
              </div>
            </div>

            {/* ── Banner stat widgets: value · rank · movement · trend ──── */}
            <div className={styles.widgetRow}>
              <StatWidget
                title="Subscribers"
                scope={scopeLine}
                unit="users"
                value={subValue}
                rank={ranks.subscribers}
                rankChange={rankMovement.subscribers}
                tone="retained"
                trend={history.map((h) => h.subscribers)}
                trendLabels={trendLabels}
                formatTrend={formatCount}
                hint={
                  <>
                    Paying subscribers in {scopeLine}. Rank {rankScope}.{qoq(changes.subscribers)}
                  </>
                }
              />
              <StatWidget
                title="Quarterly Revenue"
                scope={scopeLine}
                unit="usd"
                value={revValue}
                rank={ranks.revenue}
                rankChange={rankMovement.revenue}
                tone="revenue"
                trend={history.map((h) => h.revenue)}
                trendLabels={trendLabels}
                formatTrend={formatUsdCompact}
                hint={
                  <>
                    Subscribers × ARPU in {scopeLine}. This is platform revenue, not title-attributed.
                    {qoq(changes.revenue)}
                  </>
                }
              />
              <StatWidget
                title="ARPU"
                scope={scopeLine}
                unit="usd"
                value={arpuValue}
                rank={ranks.arpu}
                rankChange={rankMovement.arpu}
                tone="revenue"
                trend={history.map((h) => h.arpu)}
                trendLabels={trendLabels}
                formatTrend={formatArpu}
                hint={
                  <>
                    Average revenue per subscriber in {scopeLine}.{qoq(changes.arpu)}
                  </>
                }
              />
              <StatWidget
                title="Churn Rate"
                scope={scopeLine}
                value={churnValue}
                rank={ranks.churn}
                rankChange={rankMovement.churn}
                tone="loss"
                trend={churnHistory.map((h) => h.churn_rate ?? 0)}
                trendLabels={churnHistory.map((h) => prettyQuarter(h.quarter))}
                formatTrend={(v) => formatPct(v)}
                hint={
                  <>
                    Share of subscribers lost in {scopeLine}. Lower is better, so #1 has the lowest churn.
                    {changes.churnPts !== undefined &&
                      prevFormatted &&
                      ` ${Math.abs(changes.churnPts) < 0.05 ? 'Flat' : `${changes.churnPts > 0 ? 'Up' : 'Down'} ${Math.abs(changes.churnPts).toFixed(1)} pts`} on ${prevFormatted}.`}
                  </>
                }
              />
            </div>
          </div>
        </div>

        {/* Hero end sentinel — IntersectionObserver target for sticky bar */}
        <div ref={heroEndRef} className={styles.heroSentinel} aria-hidden="true" />

        {/* ── Tabs ──────────────────────────────────────────────────────── */}
        {!print && (
          <div className={styles.tabsRow}>
            <UniverseTabs
              baseRoute={`/platforms/${encodeURIComponent(platformName)}`}
              tabs={PROFILE_TABS}
              search={location.search}
            />
          </div>
        )}

        {/* ── Tab outlet ────────────────────────────────────────────────── */}
        <div className={styles.tabContent}>
          <Outlet />
        </div>
      </div>
    </ProfileContext.Provider>
  );
}

